import React, { useState, useEffect, useMemo } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Selection, Inject, Edit, Toolbar, Sort, Filter, Search  } from '@syncfusion/ej2-react-grids';
import { Cotizacion, DetalleCotizacion } from '../components';
import { Tabs, Tab, Select, MenuItem } from '@mui/material';
import axios from 'axios';
import moment from 'moment'; // Para manejar fechas
import { useMediaQuery } from '@mui/material';
import { L10n } from '@syncfusion/ej2-base';

// Configuración de traducciones en español
L10n.load({
  'es-ES': {
    grid: {
      Search: 'Buscar',           // Cambia el placeholder
      EmptyRecord: 'No hay registros para mostrar',
      // puedes personalizar más strings si quieres
    },
  },
});

const Cotizaciones = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [cotizacionVisible, setCotizacionVisible] = useState(false);
  const [selectedCotizacionId, setSelectedCotizacionId] = useState(null); // Estado para almacenar el ID
  const [detallecotizacionVisible, setDetalleCotizacionVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroCampana, setFiltroCampana] = useState('');
  const isMobile = useMediaQuery('(max-width: 768px)'); // true si es pantalla chica
  const toolbarOptions = ['Search'];

  const fetchCotizacionData = async () => {
    try { 
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('No se ha encontrado el token de autenticación. Por favor, inicia sesión.');
        return;
      }

      const campanaParam = filtroCampana.trim() ? `&campaign_codigo=${encodeURIComponent(filtroCampana.trim())}` : '';
      let response;
      switch (selectedTab) {
        case 0: // Pendiente
          response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/?estado=pendiente${campanaParam}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          break;
        case 1: // Aprobado
          response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/?estado=aprobado${campanaParam}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          break;
        case 2: // Cancelado
          response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/?estado=rechazado${campanaParam}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          break;
        case 3: // Todas las cotizaciones
        default:
          response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/?${campanaParam ? campanaParam.slice(1) : ''}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          break;
      }

      if (response && response.data) {
        setFilteredData(response.data);
      } else {
        setError('No se recibieron datos del servidor.');
      }
    } catch (error) {
      setError('Error al cargar las cotizaciones.');
      console.error('Error fetching cotizacion data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const refreshCotizaciones = () => {
    fetchCotizacionData(); // Llama a la función que vuelve a cargar las cotizaciones
    setSelectedCotizacionId(null); // Cerrar el modal
  };

  useEffect(() => {
    fetchCotizacionData();
  }, [selectedTab, filtroCampana]);


  const handleCloseDetalleCotizacion = () => {
    setError(null); // Limpia el error
    setSelectedCotizacionId(null);
    setDetalleCotizacionVisible(false);
  };

  const handleRowClick = (args) => {
    const cotizacionId = args.data.id_cotizacion; // Obtén el ID de la cotización seleccionada
    setSelectedCotizacionId(cotizacionId); // Guarda el ID en el estado
    setDetalleCotizacionVisible(true); // Muestra el componente DetalleCotizacion
  };
  
  const isOverdue = (date, status) => {
    const twoWeeksAgo = moment().subtract(14, 'days'); // Fecha límite de hace 14 días
    const recordDate = moment(date, 'DD-MM-YYYY'); // Ajustar el formato a 'DD-MM-YYYY'
    
    // Solo se marca como atrasada si es pendiente y la fecha es anterior a hace 14 días
    return status === 'pendiente' && recordDate.isBefore(twoWeeksAgo, 'day');
  };

  const handleRowDataBound = (args) => {
    if (isOverdue(args.data.fecha_cotizacion, args.data.estado)) {
      args.row.style.backgroundColor = '#FFE5E5'; // Resalta la fila en rojo claro
      args.row.style.color = '#FF0000'; // Cambia el color del texto a rojo
    }
  };
  const formattedData = useMemo(() => filteredData.map((item) => ({
    ...item,
    fecha_cotizacion: new Date(item.fecha_cotizacion),
  })), [filteredData]);

  const TabWithIcon = ({ color, label }) => (
    <div
      style={{
        display: 'inline-block',
        backgroundColor: color,
        borderRadius: '12px',
        padding: '2px 8px',
        fontSize: '12px', // Tamaño más pequeño
        color: 'white',
        fontWeight: 'bold',
        textAlign: 'center',
        minWidth: '60px',
      }}
    >
      {label}
    </div>
  );

  return (
    <div className="h-full flex flex-col p-2 md:p-4 overflow-hidden">
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-3xl p-2 md:p-3 overflow-hidden">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
        <p className="text-xl md:text-3xl font-extrabold tracking-tight text-slate-900">Cotizaciones</p>
        <div className="flex items-center gap-3">
          {isMobile ? (
            <Select
              value={selectedTab}
              onChange={(e) => setSelectedTab(e.target.value)}
              size="small"
              sx={{
                minWidth: 150,
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 },
              }}
            >
              <MenuItem value={0}><TabWithIcon color="#FEC90F" label="PENDIENTE" /></MenuItem>
              <MenuItem value={1}><TabWithIcon color="#8BE78B" label="APROBADO" /></MenuItem>
              <MenuItem value={2}><TabWithIcon color="red" label="CANCELADO" /></MenuItem>
              <MenuItem value={3}>TODAS</MenuItem>
            </Select>
          ) : (
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{
                style: { backgroundColor: '#0000FF', height: '3px' },
              }}
              sx={{
                '& .MuiTab-root': { minWidth: 'auto', padding: '0 8px' },
                '& .MuiTabs-flexContainer': { gap: '8px' },
              }}
            >
              <Tab label={<TabWithIcon color="#FEC90F" label="PENDIENTE" />} />
              <Tab label={<TabWithIcon color="#8BE78B" label="APROBADO" />} />
              <Tab label={<TabWithIcon color="red" label="CANCELADO" />} />
              <Tab label="TODAS" />
            </Tabs>
          )}
          <input
            type="text"
            value={filtroCampana}
            onChange={(e) => setFiltroCampana(e.target.value)}
            placeholder="Filtrar por campaña..."
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none w-40"
          />
          <div
            className={isMobile ? "floating-button" : "desktop-button"}
            onClick={() => setCotizacionVisible(true)}
          >
            + Cotización
          </div>
        </div>
      </div>

      {cotizacionVisible && (
        <Cotizacion
        onClose={() => setCotizacionVisible(false)}
        refreshCotizaciones={refreshCotizaciones}  // Pasa la función para refrescar las cotizaciones
      />
      )}
      {detallecotizacionVisible && (
        <DetalleCotizacion
          cotizacionId={selectedCotizacionId} // Pasamos el ID como prop
          onClose={handleCloseDetalleCotizacion} // Asegura limpiar el estado correctamente
          onAprobar={refreshCotizaciones}
        />
      )}

      <div className="flex-1 min-h-0 overflow-hidden mt-2">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-orange-400 rounded-full animate-spin" />
          <p className="text-sm">Cargando cotizaciones...</p>
        </div>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
<GridComponent
  dataSource={formattedData}
  enableHover={false}
  height="100%"
  allowPaging
  pageSettings={{ pageCount: 5, pageSize: 15 }}
  selectionSettings={{ persistSelection: true, type: 'Single' }}  // Selección de filas individuales
  locale="es-ES" 
   toolbar={toolbarOptions}
  editSettings={{ allowEditing: false, allowDeleting: false }}  // Desactivar la edición
  allowSorting
  allowMultiSorting={false} // Solo una columna a la vez
  sortSettings={{ columns: [{ field: 'fecha_cotizacion', direction: 'Descending' }] }} // Orden inicial
  className="mt-4"
  rowSelected={handleRowClick}
  rowDataBound={handleRowDataBound}
>
  <ColumnsDirective>
    {/* ID Cotización */}
    <ColumnDirective field="id_cotizacion" headerText="ID" width="100" />

    {/* Solicitante */}
    <ColumnDirective field="solicitante_nombre_completo" headerText="Solicitante" width="200" />

    {/* Fecha Cotización */}
    <ColumnDirective 
      field="fecha_cotizacion" 
      headerText="Fecha" 
      width="150" 
      format="dd/MM/yyyy" // Formato que deseas mostrar
      allowSorting={true} 
    />

    {/* Estado de Cotización con renderizado personalizado */}
    <ColumnDirective
      field="estado"
      headerText="Estado"
      width="100"
      template={(props) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {props.estado === 'pendiente' ? (
          <TabWithIcon color="#FEC90F" />
        ) : props.estado === 'aprobado' ? (
          <TabWithIcon color="#8BE78B"  />
        ) : props.estado === 'cancelado' ? (
          <TabWithIcon color="red" />
        ) : (
          props.estado
        )}
          </div>
        );
      }}
    />

    {/* Total Cotización */}
    <ColumnDirective field="total_cotizacion" headerText="Valor" width="150" format="C2" />

    {/* Ubicación */}
    <ColumnDirective field="solicitante_comuna" headerText="Ubicación" width="200" />

    {/* Dirección de Instalación */}
    <ColumnDirective field="direccion_instalacion" headerText="Dirección" width="250" />

    {/* Campaña */}
    <ColumnDirective
      field="campaign_info"
      headerText="Campaña"
      width="160"
      template={(props) =>
        props.campaign_info ? (
          <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded font-medium">
            {props.campaign_info.codigo} · -{props.campaign_info.valor_descuento}
            {props.campaign_info.tipo_descuento === 'porcentaje' ? '%' : ' CLP'}
          </span>
        ) : null
      }
    />
  </ColumnsDirective>
  <Inject services={[Page, Selection, Toolbar, Edit, Sort, Filter, Search]} />
</GridComponent>

      )}
      </div>
      </div>
    </div>
  );
};

export default Cotizaciones;
